import type { TourDefinition } from '../../../models/tour.interfaces';

export const productsTour: TourDefinition = {
  module: 'products',
  steps: [
    {
      id: 'productos_step_1',
      order: 1,
      target: '[data-testid="btn-add-product"]',
      title: 'Crea un nuevo producto',
      body: 'Haz clic aquí para registrar un producto en tu inventario.',
      requiredAction: 'click',
      nextStepTrigger: 'onElementClick',
      placement: 'bottom',
      skippable: true,
    },
    {
      id: 'productos_step_2',
      order: 2,
      target: '[data-testid="product-name"]',
      title: 'Nombre del producto',
      body: 'Escribe el nombre del producto tal como aparecerá en tu catálogo (ej: "Shampoo Hidratante 500ml").',
      completedBody: '¡Listo! Nombre ingresado. Haz clic en Siguiente.',
      requiredAction: 'fill',
      nextStepTrigger: 'onNextButton',
      placement: 'bottom',
      skippable: false,
      completionCheck: (el) => {
        const input = el.querySelector('input') as HTMLInputElement;
        return input ? input.value.trim().length > 0 : false;
      },
    },
    {
      id: 'productos_step_3',
      order: 3,
      target: '[data-testid="product-code"]',
      title: 'Código del producto',
      body: 'Asigna un código único para identificar el producto (ej: "SH-HID-500").',
      completedBody: '¡Perfecto! Código asignado. Haz clic en Siguiente.',
      requiredAction: 'fill',
      nextStepTrigger: 'onNextButton',
      placement: 'bottom',
      skippable: false,
      completionCheck: (el) => {
        const input = el.querySelector('input') as HTMLInputElement;
        return input ? input.value.trim().length > 0 : false;
      },
    },
    {
      id: 'productos_step_4',
      order: 4,
      target: '[data-testid="product-brand"]',
      title: 'Marca del producto',
      body: 'Busca una marca existente o escribe una nueva y presiona Enter para crearla.',
      completedBody: '¡Marca seleccionada! Haz clic en Siguiente.',
      requiredAction: 'fill',
      nextStepTrigger: 'onNextButton',
      placement: 'bottom',
      skippable: false,
      spotlightExtraHeight: 150,
      completionCheck: (el) => {
        // Brand is selected when there's a badge/span inside the search input area
        const brandBadge = el.querySelector('div[class*="brand"] span');
        if (brandBadge && brandBadge.textContent!.trim().length > 0) return true;
        // Fallback: check if input has value
        const input = el.querySelector('input') as HTMLInputElement;
        return input ? input.value.trim().length > 0 : false;
      },
    },
    {
      id: 'productos_step_5',
      order: 5,
      target: '[data-testid="product-category"]',
      title: 'Categoría del producto',
      body: 'Selecciona una categoría existente o escribe una nueva y presiona Enter para crearla (ej: "Cuidado Capilar").',
      completedBody: '¡Categoría asignada! Haz clic en Siguiente.',
      requiredAction: 'fill',
      nextStepTrigger: 'onNextButton',
      placement: 'top',
      skippable: false,
      spotlightExtraHeight: 150,
      completionCheck: (el) => {
        // Category selected: badge buttons appear as siblings after the target
        const parent = el.parentElement;
        if (parent) {
          const badges = parent.querySelectorAll('div[class*="categoryButton"]');
          if (badges.length > 0) return true;
        }
        return false;
      },
    },
    {
      id: 'productos_step_6',
      order: 6,
      target: '[data-testid="product-price-block"]',
      title: 'Precio de venta',
      body: 'Ingresa el precio de venta del producto. El IGV se calculará automáticamente.',
      completedBody: '¡Precio configurado! Haz clic en Siguiente.',
      requiredAction: 'fill',
      nextStepTrigger: 'onNextButton',
      placement: 'top',
      skippable: false,
      completionCheck: (el) => {
        const inputs = el.querySelectorAll('input:not([disabled])');
        let hasPrice = false;
        inputs.forEach((input: Element) => {
          const val = parseFloat((input as HTMLInputElement).value);
          if (val > 0) hasPrice = true;
        });
        return hasPrice;
      },
    },
    {
      id: 'productos_step_7',
      order: 7,
      target: '[data-testid="product-stock"]',
      title: 'Stock del producto',
      body: 'Indica cuántas unidades tienes inicialmente en tu inventario.',
      completedBody: '¡Stock registrado! Haz clic en Siguiente.',
      requiredAction: 'fill',
      nextStepTrigger: 'onNextButton',
      placement: 'top',
      skippable: false,
      completionCheck: (el) => {
        const input = el.querySelector('input') as HTMLInputElement;
        if (!input) return false;
        const val = parseFloat(input.value);
        return !isNaN(val) && val > 0;
      },
    },
    {
      id: 'productos_step_8',
      order: 8,
      target: '[data-testid="product-alert-stock"]',
      title: 'Alerta de stock',
      body: 'Define a partir de cuántas unidades el sistema debe avisarte para reponer inventario.',
      completedBody: '¡Alerta configurada! Haz clic en Siguiente.',
      requiredAction: 'fill',
      nextStepTrigger: 'onNextButton',
      placement: 'top',
      skippable: false,
      completionCheck: (el) => {
        const input = el.querySelector('input') as HTMLInputElement;
        if (!input) return false;
        const val = parseFloat(input.value);
        return !isNaN(val) && val > 0;
      },
    },
    {
      id: 'productos_step_9',
      order: 9,
      target: '[data-testid="btn-save-product"]',
      title: 'Guardar producto',
      body: 'Haz clic aquí para registrar el producto en tu inventario.',
      requiredAction: 'click',
      nextStepTrigger: 'onElementClick',
      placement: 'bottom',
      skippable: false,
    },
    {
      id: 'productos_step_10',
      order: 10,
      target: '[data-testid="product-list"]',
      title: 'Producto registrado',
      body: 'Aquí verás el producto que acabas de crear, listo para editar o eliminar.',
      requiredAction: 'none',
      nextStepTrigger: 'onFinish',
      placement: 'top',
      skippable: true,
    },
  ],
};
